import { Modal, Input, Divider } from "antd";
import { useState, useEffect, useRef } from "react";
import useAdminDataStore, { selectCurrentGroupId, selectAllGroups } from "../../../store/adminData/adminData";
import { db } from "../../../services/firebaseService/connection";
import { getWebsiteConfig } from "../../../services/firebaseService/endPoints/admin/website";
import Map from "../../commons/formFields/Map";
import { getLocations } from "../../../services/firebaseService/endPoints/admin/content";
import { ChantButton, ChantSelect } from "../../../CommonComponents";
import { doc, setDoc } from "firebase/firestore";
import { postUpdateCDNUtil } from "../../../helperFunctions/util";
import logger from "../../../utils/logger";
import { CloseBorderlessIcon } from "../../../utils/icons.jsx";
import DescriptionText from "../../../CommonComponents/DescriptionText/DescriptionText.jsx";
import AdminContainer from "../admin-container/AdminContainer.jsx";

const Sections = (props) => {
  const currentGroup = useAdminDataStore(selectCurrentGroupId);
  const allGroups = useAdminDataStore(selectAllGroups);
  const adminData = allGroups[currentGroup];

  const [newsIntro, setNewsIntro] = useState("");
  const [eventsIntro, setEventsIntro] = useState("");
  const [whereWeMeetIntro, setWhereWeMeetIntro] = useState("");
  const [currentKey, setCurrentKey] = useState("");
  const [eventLocations, setEventLocations] = useState([{}]);
  const [isSaving, setIsSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [allLocations, setAllLocations] = useState([]);
  // Dynamic state map for keys like watchparty/tailgate (used by mapUpdate)
  const [stateByKey, setStateByKey] = useState({});

  const childRef = useRef(null);

  const getState = (websiteConfig) => {
    if (websiteConfig && websiteConfig.sections) {
      let locs;

      if (
        !websiteConfig.sections.eventLocations ||
        websiteConfig.sections.eventLocations?.length === 0
      ) {
        locs = [{}];
      } else {
        locs = websiteConfig.sections.eventLocations;
      }

      return {
        newsIntro: websiteConfig.sections.newsIntro,
        eventsIntro: websiteConfig.sections.eventsIntro,
        whereWeMeetIntro: websiteConfig.sections.whereWeMeetIntro,
        eventLocations: locs,
      };
    } else {
      return {
        newsIntro: "",
        eventsIntro: "",
        whereWeMeetIntro: "",
        watchparty: {
          location: "",
          address: "",
          locationUrl: "",
          placeId: "",
          latitude: "",
          longitude: "",
          description: "",
          image: null,
        },
        tailgate: {
          location: "",
          address: "",
          placeId: "",
          latitude: "",
          longitude: "",
          locationUrl: "",
          description: "",
          image: null,
        },
      };
    }
  };

  const fetchData = () => {
    getWebsiteConfig(currentGroup).then((data) => {
      const s = getState(data);
      if (s.newsIntro !== undefined) setNewsIntro(s.newsIntro);
      if (s.eventsIntro !== undefined) setEventsIntro(s.eventsIntro);
      if (s.whereWeMeetIntro !== undefined) setWhereWeMeetIntro(s.whereWeMeetIntro);
      if (s.eventLocations !== undefined) setEventLocations(s.eventLocations);
      // Store any extra keys (watchparty, tailgate) in stateByKey
      const { newsIntro: _n, eventsIntro: _e, whereWeMeetIntro: _w, eventLocations: _l, ...rest } = s;
      if (Object.keys(rest).length > 0) {
        setStateByKey((prev) => ({ ...prev, ...rest }));
      }
    });
    getLocations(currentGroup).then((locs) => {
      setAllLocations(locs);
    });
  };

  const handleInputChange = (e) => {
    let name = e.target.name;
    if (name.includes(".")) {
      let keySections = e.target.name.split(".");
      setStateByKey((prev) => ({
        ...prev,
        [keySections[0]]: {
          ...(prev[keySections[0]] || {}),
          [keySections[1]]: e.target.value,
        },
      }));
    } else {
      if (name === "newsIntro") setNewsIntro(e.target.value);
      else if (name === "eventsIntro") setEventsIntro(e.target.value);
      else if (name === "whereWeMeetIntro") setWhereWeMeetIntro(e.target.value);
    }
  };

  const saveToFirebase = () => {
    const save = async () => {
      try {
        setIsSaving(true);

        const updateObj = {
          newsIntro: newsIntro || "",
          eventsIntro: eventsIntro || "",
          whereWeMeetIntro: whereWeMeetIntro || "",
          eventLocations: eventLocations || [{}],
        };

        const gd = adminData.data;
        const identityFields = {
          groupName: gd.groupName || "",
          clubName: gd.clubName || "",
          leagueName: gd.leagueName || "",
          country: gd.country || "",
          ...(gd.clubId ? { clubId: gd.clubId } : {}),
          ...(gd.leagueId ? { leagueId: gd.leagueId } : {}),
        };
        const docRef = doc(db, "website", gd.id);

        await setDoc(docRef, { sections: updateObj, ...identityFields }, { merge: true });

        fetchData();
        postUpdateCDNUtil(currentGroup);
      } catch (error) {
        logger.error("Error saving to Firebase:", error);
      } finally {
        setIsSaving(false);
      }
    };

    save();
  };

  const handleMapOK = () => {
    setShowModal((prev) => !prev);
    childRef.current.updateFormMapData();
  };

  const handleModalChange = (e, key) => {
    setShowModal((prev) => !prev);
    setCurrentKey(key);
  };

  const mapUpdate = (e) => {
    setStateByKey((prev) => {
      const venueData = {
        ...(prev[currentKey] || {}),
      };
      // placeId: e.placeId,
      venueData.address = (e.address + "").toString();
      venueData.placeId = (e.placeId + "").toString();
      venueData.longitude = e.long;
      venueData.latitude = e.lat;
      return { ...prev, [currentKey]: venueData };
    });
    logger.log({ stateByKey });
  };

  const addEventLocation = () => {
    setEventLocations((prev) => [...prev, {}]);
  };

  useEffect(() => {
    fetchData();
  }, [currentGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  let primaryColor = "";
  if (
    adminData.data.configuration &&
    adminData.data.configuration.primaryColor
  ) {
    let { r, g, b } = adminData.data.configuration.primaryColor;
    primaryColor = `rgb(${r}, ${g}, ${b})`;
  }

  const subLabelStyle = {
    display: "block",
    marginBottom: "6px",
    fontWeight: 500,
  };

  const infoTextStyle = {
    fontSize: 14,
    color: "#595959",
    margin: 0,
  };

  return (
    <div>
      <DescriptionText showBackground>
        Review settings to configure which sections / content will appear on
        your website.
      </DescriptionText>
      <AdminContainer>
        {showModal && (
          <Modal
            open={showModal}
            width="150vh"
            onOk={handleMapOK}
            onCancel={handleModalChange}
          >
            <Map
              onRef={(ref) => (childRef.current = ref)}
              mapUpdate={mapUpdate}
              lat={stateByKey[currentKey]?.latitude}
              long={stateByKey[currentKey]?.longitude}
              place_id={stateByKey[currentKey]?.placeId}
            />
          </Modal>
        )}

        <div className="chant-form-body">

          {/* ── News & Announcements ── */}
          <div style={{ marginBottom: "32px" }}>
            <p style={subLabelStyle}>News &amp; Announcements</p>
            <div style={{ marginBottom: "20px" }}>
              <label style={subLabelStyle}>Intro</label>
              <Input.TextArea
                maxLength={1000}
                rows={2}
                value={newsIntro}
                name="newsIntro"
                onChange={handleInputChange}
              />
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={subLabelStyle}>News &amp; Updates</label>
              <p style={infoTextStyle}>
                News and Updates are created via the &apos;Content&apos; section
                of the admin dashboard. Those set as &apos;Public on web&apos;
                will appear in this section of your website.
              </p>
            </div>
            <div>
              <label style={subLabelStyle}>Announcements</label>
              <p style={infoTextStyle}>
                Pin announcements from the app by clicking &apos;Pin to
                website&apos; on any chat post. Unpin by deselecting from the
                app or by clicking &apos;x&apos; on pinned announcements below.
              </p>
            </div>
          </div>

          <Divider />

          {/* ── Events ── */}
          <div style={{ marginBottom: "32px" }}>
            <p style={subLabelStyle}>Events</p>
            <p style={{ ...infoTextStyle, marginBottom: "16px" }}>
              Events are public on the web by default unless &apos;Show on
              web&apos; is deselected when creating the item.
            </p>
            <div>
              <label style={subLabelStyle}>Intro</label>
              <Input.TextArea
                maxLength={1000}
                rows={2}
                value={eventsIntro}
                name="eventsIntro"
                onChange={handleInputChange}
              />
            </div>
          </div>

          <Divider />

          {/* ── Where We Meet ── */}
          <div style={{ marginBottom: "32px" }}>
            <p style={subLabelStyle}>Where We Meet</p>
            <div style={{ marginBottom: "24px" }}>
              <label style={subLabelStyle}>Intro</label>
              <Input.TextArea
                maxLength={1000}
                rows={2}
                value={whereWeMeetIntro}
                name="whereWeMeetIntro"
                onChange={handleInputChange}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "12px" }}>
              {eventLocations.map((item, index) => (
                <div key={index} style={{ display: "flex", alignItems: "flex-end", gap: "12px" }}>
                  <div style={{ flex: 1 }}>
                    <ChantSelect
                      label={`Event Location #${index + 1}`}
                      value={item?.id || undefined}
                      onChange={(value) => {
                        if (value) {
                          let location = allLocations.find((l) => l.id === value);
                          let locs = eventLocations.slice();
                          locs[index] = location;
                          setEventLocations(locs);
                        } else {
                          let locs = eventLocations.slice();
                          locs.splice(index, 1);
                          setEventLocations(locs);
                        }
                      }}
                      placeholder="Select location"
                      allowClear
                      options={allLocations.map((location) => ({
                        value: location.id + "",
                        label: `${location.type}:${location.name}`,
                      }))}
                    />
                  </div>
                  {index > 0 && (
                    <div style={{ paddingBottom: "6px" }}>
                      <img
                        onClick={() => {
                          let locs = eventLocations.slice();
                          locs.splice(index, 1);
                          setEventLocations(locs);
                        }}
                        alt="delete location"
                        height={24}
                        width={24}
                        style={{ cursor: "pointer" }}
                        src={CloseBorderlessIcon}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <ChantButton type="secondary" onClick={addEventLocation}>
              + Add Event Location
            </ChantButton>
            <p style={{ ...infoTextStyle, marginTop: "10px", fontSize: 13 }}>
              Add Locations in Content / Locations section of the dashboard.
            </p>
          </div>

        </div>

        <div className="d-flex justify-content-center" style={{ marginTop: "8px" }}>
          <ChantButton
            style={{
              backgroundColor: primaryColor,
              borderColor: primaryColor,
              width: "200px",
            }}
            loading={isSaving}
            onClick={saveToFirebase}
          >
            Save
          </ChantButton>
        </div>
      </AdminContainer>
    </div>
  );
};

export default Sections;
